import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { TextInput, useGetList, useGetOne } from "react-admin";
import { BKFCourse } from "../../providers/coursesProvider";
import { Row } from "../../misc/Row";
import { type ChangeHandler, useFormContext } from "react-hook-form";
import { Column } from "../../misc/Column";
import { CourseModule, CourseTemplate } from "../../providers/courseTemplatesProvider";
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  Typography,
} from "@mui/material";
import { Form2Theme } from "../../misc/Form2";
import { SelectInput } from "../../inputs/SelectInput";
import { Room } from "../../providers/roomsProvider";
import { Instructor } from "../../providers/instructorsProvider";
import { useThemeColor } from "../../hooks/useThemeColor";
import { CheckedIcon } from "../../icons/CheckedIcon";
import { UncheckedOutlineIcon } from "../../icons/UncheckedOutlineIcon";
import { TinyColor } from "@ctrl/tinycolor";
import { getScheduledUeForModule } from "./getScheduledUeForModule";

export interface CoursePlanningFormValues {
  module: null | CourseModule;
  lecturerId: null | string;
  roomId: null | string;
  noteForParticipants: string;
}

const INITIAL_MODULE_SELECTION_DELAY_MS = 120;

export function CoursePlanningForm(props: { course: BKFCourse; isFreeSlotPreviewHovered?: boolean }) {
  const { course, isFreeSlotPreviewHovered = false } = props;
  return (
    <Form2Theme>
      <Column>
        <Typography variant="h6">Kurstermine planen</Typography>
        <Typography variant="body2" sx={{ my: 2, color: "text.secondary" }}>
          Kursteil wählen und im Kalender auf einen freien Slot klicken.
        </Typography>
        <_ModuleSelect course={course} isFreeSlotPreviewHovered={isFreeSlotPreviewHovered} />
      </Column>
    </Form2Theme>
  );
}

function _ModuleSelect(props: { course: BKFCourse; isFreeSlotPreviewHovered: boolean }) {
  const { course, isFreeSlotPreviewHovered } = props;
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CoursePlanningFormValues>();
  const { onChange } = register("module");
  const selectedModule = watch("module");
  const error = errors["module"]?.message;
  const { data: courseTemplate } = useGetOne<CourseTemplate>("courseTemplates", { id: course.courseTemplateId });

  useEffect(() => {
    if (!courseTemplate?.modules.length) {
      return;
    }
    if (selectedModule && courseTemplate.modules.some((module) => module.name === selectedModule.name)) {
      return;
    }
    const firstModuleWithRemainingUe = courseTemplate.modules.find((module) => {
      const ueScheduled = getScheduledUeForModule(course.events, module.name);
      return ueScheduled < module.ue;
    });
    const preferredModule = firstModuleWithRemainingUe ?? courseTemplate.modules[0];

    const timeoutId = window.setTimeout(() => {
      setValue("module", preferredModule);
    }, INITIAL_MODULE_SELECTION_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [course, courseTemplate, selectedModule, setValue]);

  return (
    <FormControl>
      <InputLabel error={!!error}>Kursteil</InputLabel>
      <Column gap={1}>
        {courseTemplate &&
          courseTemplate.modules.map((module) => (
            <_CourseModule
              key={module.name}
              course={course}
              module={module}
              onChange={onChange}
              shouldZoom={isFreeSlotPreviewHovered}
            />
          ))}
      </Column>
      <FormHelperText error={!!error}>{error ?? "\u00A0"}</FormHelperText>
    </FormControl>
  );
}

function _CourseModule(props: {
  course: BKFCourse;
  module: CourseModule;
  onChange: ChangeHandler;
  shouldZoom: boolean;
}) {
  const { course, module, onChange, shouldZoom } = props;
  const { watch, clearErrors } = useFormContext<CoursePlanningFormValues>();
  const selectedModule = watch("module");
  const primaryColor = useThemeColor("primary");
  const ueScheduled = useMemo(() => getScheduledUeForModule(course.events, module.name), [course, module]);
  const ueTotal = module.ue;
  const isSelected = module.name === selectedModule?.name;
  const shouldHighlightSelection = isSelected && shouldZoom;
  const outlineColor = shouldHighlightSelection ? module.color : isSelected ? primaryColor.main : "divider";
  const highlightBackgroundColor = new TinyColor(module.color).setAlpha(0.1).toRgbString();
  return (
    <Box
      sx={{
        border: 2,
        borderColor: outlineColor,
        ...(isSelected ? {} : { "&:hover": { borderColor: "text.primary" } }),
        borderRadius: "8px",
        py: 1,
        px: 2,
        cursor: "pointer",
        position: "relative",
        transition: "background-color 140ms ease-out",
        backgroundColor: shouldHighlightSelection ? highlightBackgroundColor : "transparent",
      }}
    >
      <Column gap={1}>
        <Row
          gap={1}
          onClick={() => {
            void onChange({ target: { name: "module", value: module }, type: "change" });
            clearErrors("module");
          }}
          sx={{ alignItems: "center" }}
        >
          <_CircularProgress ueScheduled={ueScheduled} ueTotal={ueTotal} color={module.color} />
          <Column sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
              {module.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {`${ueScheduled} / ${ueTotal} UE geplant`}
            </Typography>
          </Column>
          {isSelected ? (
            <CheckedIcon sx={{ color: primaryColor.main }} />
          ) : (
            <UncheckedOutlineIcon sx={{ color: "divider" }} />
          )}
        </Row>
        <Collapse in={isSelected} timeout={220} unmountOnExit>
          <Column gap={1} sx={{ pt: 1 }}>
            <_LecturerSelect drivingSchoolId={course.drivingSchoolId} />
            <_RoomSelect drivingSchoolId={course.drivingSchoolId} branchId={course.branchId} />
            <_NoteForParticipantsInput />
            <Box sx={{ height: 1 }} />
          </Column>
        </Collapse>
      </Column>
    </Box>
  );
}

function _NoteForParticipantsInput() {
  const noteForParticipants = useFormContext<CoursePlanningFormValues>().watch("noteForParticipants");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const shouldShowNoteInput = showNoteInput || !!noteForParticipants;

  if (!shouldShowNoteInput) {
    return (
      <Link
        component="button"
        onClick={() => setShowNoteInput(true)}
        underline="hover"
        sx={{ textAlign: "left", fontSize: "14px" }}
      >
        + Hinweis für Teilnehmer
      </Link>
    );
  }

  return (
    <TextInput
      source="noteForParticipants"
      label="Hinweis für Teilnehmer"
      multiline
      rows={2}
      size="small"
      sx={{ "& .MuiFormHelperText-root": { display: "none" } }}
    />
  );
}

function _CircularProgress(props: { ueScheduled: number; ueTotal: number; color: string }) {
  const { ueScheduled, ueTotal, color } = props;
  let value = (ueScheduled / ueTotal) * 100;
  if (typeof value !== "number" || !isFinite(value)) {
    value = 0;
  } else {
    value = Math.max(0, Math.min(100, value));
  }
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <CircularProgress
        variant="determinate"
        size={33}
        thickness={8}
        value={100}
        sx={{ position: "absolute", color: "divider" }}
      />
      <CircularProgress variant="determinate" size={33} thickness={8} value={value} sx={{ color }} />
    </div>
  );
}

function _LecturerSelect(props: { drivingSchoolId: string }) {
  const { drivingSchoolId } = props;
  const { data: instructors } = useGetList<Instructor>("instructors", {
    filter: { drivingSchoolId },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "name", order: "ASC" },
  });
  const options: Array<[string, string]> = useMemo(
    () => (instructors ?? []).map((instructor) => [instructor.autovioUserId, instructor.name]),
    [instructors],
  );
  return (
    <SelectInput
      source="lecturerId"
      label="Dozent"
      size="small"
      options={options}
      displayEmpty
      renderValue={(selectedValue) => {
        const selectedLecturer = options.find(([lecturerId]) => lecturerId === selectedValue)?.[1];
        if (selectedLecturer) {
          return selectedLecturer;
        }
        return <Typography color="text.disabled">Ohne Dozent</Typography>;
      }}
    />
  );
}

function _RoomSelect(props: { drivingSchoolId: string; branchId: string }) {
  const { drivingSchoolId, branchId } = props;
  const { data: rooms } = useGetList<Room>("rooms", {
    filter: { drivingSchoolId, branchId },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "name", order: "ASC" },
  });
  const options: Array<[string, string]> = useMemo(() => (rooms ?? []).map((room) => [room.id, room.name]), [rooms]);
  return (
    <SelectInput
      source="roomId"
      label="Raum"
      size="small"
      options={options}
      displayEmpty
      renderValue={(selectedValue) => {
        const selectedRoom = options.find(([roomId]) => roomId === selectedValue)?.[1];
        if (selectedRoom) {
          return selectedRoom;
        }
        return <Typography color="text.disabled">Ohne Raum</Typography>;
      }}
    />
  );
}
